import React from 'react';
import { Button } from 'antd';
import { Info, MapPin, ExternalLink } from 'lucide-react';
import { StatusTag, CollapsibleCard } from '../../common';
import { calculateAuditDuration, isDurationWarning, fmtStoreCheck } from '../../../utils/timeUtils';

/** Small labeled block inside the section. */
function InfoBlock({ label, children, className = '' }) {
    return (
        <div className={`rounded-lg border border-surface-200 px-4 py-3 ${className}`}>
            <span className="block mb-1 text-xs font-medium text-surface-500">{label}</span>
            {children}
        </div>
    );
}

export default function AdditionalInfo({ storeCheck }) {
    const coordinates = storeCheck?.coordinates;
    const hasCoordinates = coordinates?.latitude && coordinates?.longitude;
    const duration = storeCheck?.duration ?? calculateAuditDuration(storeCheck);

    return (
        <CollapsibleCard title="Información Adicional" icon={<Info />} iconTone="violet">
            <div className="grid gap-4 grid-cols-1 md:grid-cols-3 mb-4">
                <InfoBlock label="Inicio">
                    <span className="text-sm font-semibold text-surface-900 tabular-nums">{storeCheck?.startTime ? fmtStoreCheck(storeCheck.startTime) : '--'}</span>
                </InfoBlock>
                <InfoBlock label="Fin">
                    <span className="text-sm font-semibold text-surface-900 tabular-nums">{storeCheck?.endTime ? fmtStoreCheck(storeCheck.endTime) : '--'}</span>
                </InfoBlock>
                <InfoBlock label="Duración">
                    {duration <= 0 ? (
                        <span className="text-sm font-semibold text-surface-900">--</span>
                    ) : (
                        <StatusTag tone={isDurationWarning(duration) ? 'warning' : 'success'} className="tabular-nums">
                            {duration} min
                        </StatusTag>
                    )}
                </InfoBlock>
            </div>

            {/* Coordinates */}
            <InfoBlock label="Coordenadas" className="mb-4">
                {hasCoordinates ? (
                    <div className="flex items-center gap-3 flex-wrap">
                        <span className="inline-flex items-center gap-2 text-sm text-surface-900">
                            <MapPin className="text-success-fg" />
                            <code className="rounded-md bg-surface-100 px-1.5 py-0.5 font-mono text-xs tabular-nums">
                                {coordinates.latitude.toFixed(6)}, {coordinates.longitude.toFixed(6)}
                            </code>
                        </span>
                        <Button
                            type="link"
                            size="small"
                            icon={<ExternalLink />}
                            iconPosition="end"
                            href={`https://www.google.com/maps?q=${coordinates.latitude},${coordinates.longitude}`}
                            target="_blank"
                            rel="noopener noreferrer">
                            Ver en mapa
                        </Button>
                    </div>
                ) : (
                    <span className="text-sm text-surface-400">Sin coordenadas</span>
                )}
            </InfoBlock>

            {/* Observations */}
            <InfoBlock label="Observaciones">
                {storeCheck?.observations ? (
                    <p className="m-0 text-sm text-surface-700 whitespace-pre-wrap">{storeCheck.observations}</p>
                ) : (
                    <span className="text-sm text-surface-400">Sin observaciones</span>
                )}
            </InfoBlock>
        </CollapsibleCard>
    );
}
